"use client";

import { notifyOutOfStockProduct } from "@/api/saleor/services/wishlist";
import FavIcon from "@/assets/icons/favorite.svg";
import FavIconFilled from "@/assets/icons/heartIconFilled.svg";
import ShareIcon from "@/assets/icons/share.svg";
import NoImage from "@/assets/images/no_image.svg";
import { Product, TranslationData, VariantItemPdp } from "@/common/lib/types";
import NotifyInStock from "@/components/common/NotifyInStock";
import Button from "@/components/ui/button/Button";
import { useCustomToast } from "@/hooks/useCustomToast";
import { cn } from "@/lib/tailwind";
import { useGlobalStore } from "@/store/store";
import { getChannelIdInClient, isNonTransactionalCountry } from "@/utils/common";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import LensConfiguratorWrapper from "../lcp/LCPWrapper";
import { applicableProductTypes } from "../lcp/resource/config";
import ProductPrice from "../productPrice/ProductPrice";
import { IPowerList } from "@/utils/product.utils";
import { usePdpContext } from "@/providers/PdpProvider";
import { useRouter } from "next/navigation";
import { startPageTransition } from "@/utils/pageLoader.utils";
import { getAttributeValue } from "@/apiUtils/productUtils";
import { SaleorAttributes } from "@/apiUtils/constants";
import PairEyewearTopModal from "@/components/pairEyewear/PairEyewearTopModal";
import { PairEyewearTopsContent } from "@/common/lib/types";

interface IProps {
  productImage: string | undefined;
  lang: "ar" | "en";
  label: string;
  locale: string;
  findStoreLabel: string;
  source?: string;
  product: Product;
  sourceId?: string;
  hitIndex?: number;
  quantity: number;
  outOfStockLabel: string;
  variant?: VariantItemPdp;
  secondaryVariant?: VariantItemPdp;
  queryID?: string;
  translations: TranslationData;
  className?: string;
  // pricing: Pricing;
  priceInfo: {
    currency: string;
    price: number;
    originalPrice: number;
    hasDiscount?: boolean;
    discountValue?: string | number;
    discountPercent?: number;
    discountPercentage?: string;
  };
  handleAddToCart: (variantId?: string, secondaryVariantId?: string, options?: { suppressToast?: boolean }) => Promise<void>;
  handleShare: () => void;
  wishlistProps: {
    isWishlisted: boolean;
    handleAddToWishlist: () => void;
    handleRemoveFromWishlist: () => void;
    setIsWishlisted: (val: boolean) => void;
  };
  productType: string;
  outOfStock: boolean;
  lensConfiguratorImageURL: string;
  handleVariantSelection: (variantId: string) => void;
  handlePowerSelectionStatus: (isSelected: boolean) => void;
  powerList: Record<string, IPowerList[]>;
  selectedColor: string;
  isClLens: boolean;
  isLensProduct?: boolean;
  isFree: boolean;
  isPairEyewear?: boolean;
  isBundle?: boolean;
  isClipOn?: boolean;
  distinctTopsStyles?: { id: string; name: string }[];
  model?: string;
  brand?: string;
  pairEyewearTopsContent?: PairEyewearTopsContent | null;
}

const AddToCart = ({
  lang,
  label,
  findStoreLabel,
  locale,
  source,
  product,
  productImage,
  variant,
  secondaryVariant,
  queryID,
  quantity,
  hitIndex,
  sourceId,
  translations,
  outOfStockLabel,
  className,
  priceInfo,
  handleAddToCart,
  handleShare,
  wishlistProps,
  productType,
  outOfStock,
  lensConfiguratorImageURL,
  handlePowerSelectionStatus,
  handleVariantSelection,
  powerList,
  selectedColor,
  isClLens,
  isLensProduct = false,
  isFree,
  isPairEyewear = false,
  isBundle = false,
  isClipOn = false,
  distinctTopsStyles = [],
  model = "",
  brand = "",
  pairEyewearTopsContent = null,
}: IProps) => {
  const router = useRouter();
  const { wishlistedProducts, cartVariantIds } = useGlobalStore((state) => state);
  const { lensSelectionTabIndex } = usePdpContext();
  const [pendingAddToCart, setPendingAddToCart] = useState(false);
  const { showToast } = useCustomToast();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [isNotifyMeLoading, setIsNotifyMeLoading] = useState(false);
  const [topsModalOpen, setTopsModalOpen] = useState(false);
  const [isAddingFrameToCart, setIsAddingFrameToCart] = useState(false);
  const [deferredFrameToast, setDeferredFrameToast] = useState<{ imgUrl: string; text: string } | null>(null);

  const isNonTransactional = useMemo(() => isNonTransactionalCountry(locale), [locale]);
  const isBlueLightCut = getAttributeValue(variant?.attributes, SaleorAttributes.BLUE_LIGHT_CUT);
  const isPhotochromic = getAttributeValue(variant?.attributes, SaleorAttributes.PHOTOCHROMIC);

  //state
  // For contact lenses, use the outOfStock prop which reflects all power variants
  // For other products, check individual variants
  const isSecondaryOutOfStock = secondaryVariant
    ? (secondaryVariant.quantityAvailable ?? 0) <= 0
    : false;
  const isOutOfStock = isLensProduct
    ? outOfStock
    : (variant?.quantityAvailable ?? 0) <= 0 || isSecondaryOutOfStock || outOfStock;

  const handleNotifyMe = async (email: string) => {
    try {
      setIsNotifyMeLoading(true);
      const channel = getChannelIdInClient(locale as string);

      const response = await notifyOutOfStockProduct(
        email,
        variant?.id!,
        channel,
        variant?.landingPageBaseUrl!,
        lang,
      );

      if (response) {
        setNotifyModalOpen(false);
      }

      if (response?.errors?.length > 0) {
        //TO DO when proper error handling with error code is handled in backend check for error code
        if (response?.errors[0]?.message === "Subscription already exists.") {
          showToast({
            text: translations?.pdp?.pdp_subscription_already_exists_for_this_email_message ?? "",
            imgUrl: productImage,
            lang,
          });

          return translations?.pdp?.pdp_subscription_already_exists_for_this_email_message
            ? translations?.pdp?.pdp_subscription_already_exists_for_this_email_message
            : "Subscription already exists for this email";
        } else {
          showToast({
            text: translations?.pdp?.pdp_something_bad_happened_message ?? "",
            imgUrl: productImage,
            lang,
          });

          return translations?.pdp?.pdp_something_bad_happened_message
            ? translations?.pdp?.pdp_something_bad_happened_message
            : "Something bad happened";
        }
      } else {
        showToast({
          text: translations?.pdp?.pdp_subscription_successful_message ?? "",
          imgUrl: productImage,
          lang,
        });

        return translations?.pdp?.pdp_subscription_successful_message
          ? translations?.pdp?.pdp_subscription_successful_message
          : "Subscription successful";
      }
    } catch (error) {
      console.log("Notify me error", error);
    } finally {
      setIsNotifyMeLoading(false);
    }
  };

  // getting the zero power lens from lens list
  const noPowerLens = useMemo(() => {
    return powerList[selectedColor]?.find((v) => v.power === "0.00");
  }, [selectedColor, lensSelectionTabIndex]);

  const handleLocalAddToCart = async () => {
    // sets zero power varintant as selected variant
    if (lensSelectionTabIndex === 0 && isClLens && noPowerLens) {
      handleVariantSelection(noPowerLens?.id ?? "");
      handlePowerSelectionStatus(true);
      setPendingAddToCart(true);
    } else {
      // handlePowerSelectionStatus(false);
      setIsAddingToCart(true);
      await handleAddToCart();
      setIsAddingToCart(false);
    }
  };

  const handleFindStore = () => {
    startPageTransition();
    router.push("/find-store");
  };

  const handleAddToCartAndSelectTops = async () => {
    if (!isOutOfStock) {
      setIsAddingFrameToCart(true);
      await handleAddToCart(undefined, undefined, { suppressToast: true });
      setDeferredFrameToast({
        imgUrl: productImage ?? "",
        text: translations?.common?.common_added_to_cart_toast_message ?? "",
      });
      setIsAddingFrameToCart(false);

      if (isPairEyewear) {
        setTopsModalOpen(true);
      }
    }
  };

  const handleCloseTopsModal = () => {
    setTopsModalOpen(false);
    setDeferredFrameToast(null);
  };

  useEffect(() => {
    const addCart = async () => {
      if (lensSelectionTabIndex === 0 && isClLens && pendingAddToCart) {
        setPendingAddToCart(false);
        setIsAddingToCart(true);
        await handleAddToCart();
        setIsAddingToCart(false);
      }
    };

    addCart();
  }, [variant]);

  // console.log({ pendingAddToCart, variant });

  //hooks
  useEffect(() => {
    wishlistProps?.setIsWishlisted(!!wishlistedProducts?.includes(variant?.id || ""));
  }, []);

  return (
    <>
      <div className={cn(className)}>
        <div className="hidden md:block border-b py-[24px]">
          <div className="flex items-center gap-4">
            {isNonTransactional
              ? !isFree && (
                  <Button
                    className="flex h-[3.125rem] items-center justify-center whitespace-nowrap md:w-[12rem] lg:w-[15rem] xl:w-[23.438rem]"
                    onClick={handleFindStore}
                  >
                    {findStoreLabel || "Find a Store"}
                  </Button>
                )
              : !isOutOfStock &&
                applicableProductTypes?.includes(product?.productType?.slug || "") &&
                !isBlueLightCut &&
                !isPhotochromic
                ? !isFree && (
                    <LensConfiguratorWrapper
                      locale={locale}
                      source={source}
                      product={product}
                      variant={variant}
                      hitIndex={hitIndex}
                      sourceId={sourceId}
                    queryID={queryID}
                      toastToggle={() => ({})}
                      translations={translations}
                      lensConfiguratorImageURL={lensConfiguratorImageURL}
                      pairEyewearTopsContent={pairEyewearTopsContent}
                    >
                      <Button className="flex h-[3.125rem] items-center justify-center whitespace-nowrap">
                        {translations?.pdp?.pdp_select_lens_and_purchase_button ??
                          "Select Lenses and Purchase"}
                      </Button>
                    </LensConfiguratorWrapper>
                  )
                : !isFree && (
                    <Button
                      variant={"contained"}
                      isLoading={isAddingToCart}
                      className="flex h-[3.125rem] items-center justify-center whitespace-nowrap md:w-[12rem] lg:w-[15rem] xl:w-[23.438rem]"
                      onClick={!isOutOfStock ? handleLocalAddToCart : () => setNotifyModalOpen(true)}
                    >
                      {!isOutOfStock ? label : outOfStockLabel}
                    </Button>
                  )}
            {!isFree && (
              <button
                className="flex size-[3.125rem] items-center justify-center border border-tertiary-5 bg-tertiary-5 px-[8.93px] py-[10.71px] hover:text-black active:bg-tertiary-5 active:bg-white"
                onClick={
                  !wishlistProps?.isWishlisted
                    ? () => wishlistProps?.handleAddToWishlist()
                    : () => wishlistProps?.handleRemoveFromWishlist()
                }
              >
                {!wishlistProps?.isWishlisted ? (
                  <Image
                    src={FavIcon}
                    alt={lang === "en" ? "favorites" : "المفضلة"}
                    className="h-[26.79px] w-[32.14px]"
                  />
                ) : (
                  <Image
                    src={FavIconFilled}
                    alt={lang === "en" ? "favorites" : "المفضلة"}
                    className="h-[26.79px] w-[32.14px]"
                  />
                )}
              </button>
            )}

            {!isFree && (
              <button
                className="flex size-[3.125rem] items-center justify-center border border-tertiary-5 bg-tertiary-5 px-[8.93px] py-[10.71px] hover:bg-tertiary-5 hover:text-black active:bg-white"
                onClick={() => handleShare()}
              >
                <Image
                  src={ShareIcon}
                  alt={
                    lang === "en" ? "Share with your friends and family" : "شارك مع أصدقائك وعائلتك"
                  }
                  className="h-[26.79px] w-[32.14px]"
                />
              </button>
            )}
          </div>
          {/* Add to Cart and Select Tops - separate row for desktop */}
          {isPairEyewear && !isBundle && !isClipOn && !isFree && (
            <div className="mt-4 ml-12">
              <button
                disabled={isAddingFrameToCart}
                className="font-semibold text-sm leading-none tracking-[-0.3px] underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={!isOutOfStock ? handleAddToCartAndSelectTops : () => setNotifyModalOpen(true)}
              >
                {translations?.pdp?.pdp_add_to_cart_and_select_tops_button}
              </button>
            </div>
          )}
          {!isFree && isPairEyewear && isBundle && (
            <div className="mt-4 ml-24">
              <button
                disabled={isAddingToCart}
                className="font-semibold text-sm leading-none tracking-[-0.3px] underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={!isOutOfStock ? handleLocalAddToCart : () => setNotifyModalOpen(true)}
              >
                {!isOutOfStock ? label : outOfStockLabel}
              </button>
            </div>
          )}
        </div>
        <div className="fixed bottom-0 left-0 z-[1] w-full bg-[#F1EBE3] md:hidden">
          <div className="flex flex-col px-3 py-2">
            <div className="flex h-full w-full items-center justify-between gap-0">
              <div className="">
                <ProductPrice
                  mobile={true}
                  className="text-sm md:text-lg"
                  locale={locale}
                  priceInfo={{
                    currency: priceInfo?.currency,
                    price: priceInfo?.price,
                    originalPrice: priceInfo?.originalPrice,
                    hasDiscount: priceInfo?.hasDiscount,
                    discountValue: priceInfo?.discountValue,
                    discountPercent: priceInfo?.discountPercent,
                  }}
                  translations={translations}
                  productType={productType}
                />
              </div>
              {isNonTransactional
                ? !isFree && (
                    <Button
                      className="flex h-[3.125rem] items-center justify-center whitespace-nowrap md:w-[12rem] lg:w-[15rem] xl:w-[23.438rem]"
                      onClick={handleFindStore}
                    >
                      {findStoreLabel || "Find Store"}
                    </Button>
                  )
                : !isOutOfStock &&
                  applicableProductTypes?.includes(product?.productType?.slug || "") &&
                  !isBlueLightCut &&
                  !isPhotochromic
                  ? !isFree && (
                      <LensConfiguratorWrapper
                        locale={locale}
                        source={source}
                        product={product}
                        variant={variant}
                        hitIndex={hitIndex}
                        sourceId={sourceId}
                        toastToggle={() => ({})}
                        translations={translations}
                        lensConfiguratorImageURL={lensConfiguratorImageURL}
                        pairEyewearTopsContent={pairEyewearTopsContent}
                      >
                        <Button className="flex h-[3.125rem] items-center justify-center text-sm max-sm:min-w-60">
                          {translations?.pdp?.pdp_select_lens_and_purchase_button ??
                            "Select Lenses and Purchase"}
                        </Button>
                      </LensConfiguratorWrapper>
                    )
                  : !isFree && (
                      <Button
                        variant={"contained"}
                        isLoading={isAddingToCart}
                        className="flex h-[3.125rem] min-w-60 items-center justify-center text-sm"
                        onClick={
                          !isOutOfStock ? handleLocalAddToCart : () => setNotifyModalOpen(true)
                        }
                      >
                        {!isOutOfStock ? label : outOfStockLabel}
                      </Button>
                    )}
            </div>
            {/* Add to Cart and Select Tops - separate row for mobile */}
            {isPairEyewear && !isBundle && !isClipOn && !isFree && (
              <div className="mt-1 flex justify-end mr-8">
                <button
                  disabled={isAddingFrameToCart}
                  onClick={!isOutOfStock ? handleAddToCartAndSelectTops : () => setNotifyModalOpen(true)}
                  className="font-semibold text-sm leading-none tracking-[-0.3px] underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {translations?.pdp?.pdp_add_to_cart_and_select_tops_button}
                </button>
              </div>
            )}
            {!isFree && isPairEyewear && isBundle && (
              <div className="mt-1 flex justify-end mr-20">
                <button
                  disabled={isAddingToCart}
                  onClick={!isOutOfStock ? handleLocalAddToCart : () => setNotifyModalOpen(true)}
                  className="font-semibold text-sm leading-none tracking-[-0.3px] underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {!isOutOfStock ? label : outOfStockLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <NotifyInStock
        imgURL={productImage ?? NoImage}
        open={notifyModalOpen}
        onAccept={handleNotifyMe}
        onCancel={() => setNotifyModalOpen(false)}
        translations={translations}
        isNotifyMeLoading={isNotifyMeLoading}
      />

      {/* Tops Selection Modal for Pair Eyewear */}
      {isPairEyewear && (
        <PairEyewearTopModal
          isOpen={topsModalOpen}
          onClose={handleCloseTopsModal}
          pairEyewearTopsContent={pairEyewearTopsContent}
          translations={translations}
          locale={locale}
          missingTopModel={[{ model }]}
          variantIdsToExcludeFromAddedCount={variant?.id ? [variant.id] : undefined}
          deferredFrameToast={deferredFrameToast}
        />
      )}
    </>
  );
};

export default AddToCart;
